import React, { useEffect, useMemo, useState } from 'react';
import { WithStyles } from '@material-ui/styles';
import {
  Box, Dialog, Grid, IconButton, Paper, TextField, Typography,
} from '@material-ui/core';
import clonedeep from 'lodash.clonedeep';
import Spinner from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import styles from './styles';
import { HubLockDevicePropertyUserCodes, HubLockUserCodeUpdate } from '../../redux/types/hubs';
import CustomButton from '../CustomButton';
import { getFreeLockCodeKey, getHubLockUserCodesForUpdate } from './utils';
import { beautifyErrors, ValidationErrors } from '../../utils/helpers';
import { lockCodeSchema } from './validation';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  userCodes: HubLockDevicePropertyUserCodes | null
  updating: boolean
  deleteHubLockCode: (code: string) => void
  updateHubLockCodes: (payload: HubLockUserCodeUpdate[]) => void
} & WithStyles<typeof styles>;

type LockCode = {
  name: string;
  code: string;
};

const HubLockUserCodesForm: React.FC<Props> = (props: Props) => {
  const {
    classes, userCodes, deleteHubLockCode, updating, updateHubLockCodes,
  } = props;

  const userCodesArrInitial = useMemo<HubLockUserCodeUpdate[]>(() => (
    userCodes
      ? getHubLockUserCodesForUpdate(userCodes)
      : []),
  [userCodes]);

  const [userCodesArr, setUserCodesArr] = useState<HubLockUserCodeUpdate[]>(clonedeep(userCodesArrInitial));
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [newCode, setNewCode] = useState<LockCode | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrors<LockCode>
  >({});
  const { canEdit } = usePermissions();

  const handleNewCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!newCode || (name === 'code' && Number.isNaN(Number(value)))) {
      return;
    }

    setNewCode({ ...newCode, [name]: value });
  };

  const saveNewCode = () => {
    try {
      if (!newCode) {
        return;
      }

      lockCodeSchema.validateSync(newCode, {
        abortEarly: false,
      });

      setValidationErrors({});

      const existedCode = userCodesArr.find((code) => code.code === newCode.code);

      if (existedCode) {
        setValidationErrors({
          code: `code ${newCode.code} already exists as ${existedCode.name}`,
        });
        return;
      }

      const newArr: HubLockUserCodeUpdate[] = clonedeep(userCodesArr);
      newArr.push({
        name: newCode.name,
        code: newCode.code,
        key: getFreeLockCodeKey(userCodesArr),
      });

      updateHubLockCodes(newArr);

      setNewCode(null);
      setOpenModal(false);
    } catch (errors) {
      setValidationErrors(beautifyErrors(errors));
    }
  };

  const deleteCode = (codeKey: string) => {
    if (!canEdit) return;
    deleteHubLockCode(codeKey);
  };

  useEffect(() => {
    setUserCodesArr(userCodesArrInitial);
  }, [userCodesArrInitial]);

  const addNewCodeButton = () => (
    <CustomButton
      variant="orange"
      disabled={updating || !canEdit}
      onClick={() => {
        setOpenModal(true);
        setNewCode({ code: '', name: '' });
      }}
      style={{
        width: '30%',
      }}
    >
      Add New Code
    </CustomButton>
  );

  return (
    <>
      {userCodesArr.length > 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%', position: 'relative' }}>
            <Box display="flex" alignItems="center" mb={4} justifyContent="space-between">
              <Typography variant="h6" className={classes.title}>Lock Codes</Typography>
            </Box>

            {userCodesArr.map((item) => (
              <Grid
                container
                alignItems="center"
                spacing={2}
                className={classes.codeCard}
                key={item.key}
              >
                <Grid item xs={5}>
                  <Typography variant="caption" className={classes.codeLabel}>
                    CODE
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    className={classes.codeValue}
                    noWrap
                  >
                    {item.code}
                  </Typography>
                </Grid>

                <Grid item xs={5}>
                  <Typography variant="caption" className={classes.codeLabel}>
                    NAME
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    className={classes.codeValue}
                    noWrap
                  >
                    {item.name}
                  </Typography>
                </Grid>

                <Grid item xs={2}>
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      disabled={!canEdit}
                      size="small"
                      onClick={() => {
                        if (!item?.key) {
                          return;
                        }

                        deleteCode(item.key);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            ))}
            {userCodesArr.length < 8 && (
              <Box mt={3} justifyContent="center" display="flex" width="100%">
                {addNewCodeButton()}
              </Box>
            )}
            {updating && (
              <Box>
                <Spinner className={classes.progress} />
              </Box>
            )}
          </Paper>
        </Grid>
      )}

      {userCodesArr.length === 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 32, height: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
            >
              <Typography variant="h6" gutterBottom>
                Lock has no codes
              </Typography>

              <Box mt={3} justifyContent="center" display="flex" width="100%">
                {addNewCodeButton()}
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          className: classes.modalWrapper,
        }}
      >
        <Typography variant="h6" gutterBottom className={classes.modalTitle}>New Smart Lock Code</Typography>

        <Grid container spacing={2} className={classes.fieldWrapper}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Code"
              variant="outlined"
              size="small"
              margin="none"
              name="code"
              className={classes.textField}
              inputProps={{ maxLength: 8 }}
              disabled={!canEdit}
              value={newCode?.code}
              onChange={handleNewCode}
              error={Boolean(validationErrors.code)}
              helperText={validationErrors.code}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Name"
              variant="outlined"
              size="small"
              margin="none"
              name="name"
              className={classes.textField}
              inputProps={{ maxLength: 16 }}
              disabled={!canEdit}
              value={newCode?.name}
              onChange={handleNewCode}
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />
          </Grid>
        </Grid>

        <Box className={classes.buttonWrapper}>
          <CustomButton
            variant="orange"
            fullWidth
            onClick={saveNewCode}
            disabled={!canEdit}
          >
            Save
          </CustomButton>

          <CustomButton
            variant="white"
            fullWidth
            onClick={() => {
              setOpenModal(false);
              setNewCode(null);
            }}
          >
            Cancel
          </CustomButton>
        </Box>
      </Dialog>
    </>
  );
};

export default HubLockUserCodesForm;
